/**
 * The investor's way into a conversation — the entry point that never existed.
 *
 * `useCreateConversation` had exactly two callers before this, both founder-side,
 * so an investor who marked a startup 'interested' had no way to act on it. That
 * is why `conversations` held zero rows in production.
 *
 * The button only appears once the investor has actually signalled interest.
 * That threshold is enforced in the `conversations` INSERT policy as well — this
 * is the affordance, not the permission, and a hidden button is not a control.
 */

import React, { useState } from 'react';
import { View, Text, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { MessageCircle } from 'lucide-react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import { handleError } from '@/lib/errors';
import { useAlert } from '@/components/AlertDialog';
import { useCreateConversation } from '@/lib/supabase';
import {
  canOpenConversation,
  conversationClosedReason,
  type DealflowStatus,
} from '@/lib/messaging/conversation-gate';

interface Props {
  startupId: string;
  startupName: string;
  /** investor_profiles.id — the space `conversations.investor_profile_id` keys on. */
  investorProfileId: string | null;
  dealflowStatus: DealflowStatus | null;
  dataRoomAccessGranted: boolean;
}

export function MessageFounderButton({
  startupId,
  startupName,
  investorProfileId,
  dealflowStatus,
  dataRoomAccessGranted,
}: Props) {
  const colors = useThemeColors();
  const router = useRouter();
  const { error: showError } = useAlert();
  const createConversation = useCreateConversation();
  const [busy, setBusy] = useState(false);

  const eligibility = { dealflowStatus, dataRoomAccessGranted };
  const open = canOpenConversation(eligibility);
  const closedReason = conversationClosedReason(eligibility);

  const handlePress = async () => {
    if (!investorProfileId) {
      showError('Profile Loading', 'Your investor profile is still loading. Try again in a moment.');
      return;
    }
    setBusy(true);
    try {
      await createConversation.mutateAsync({ startupId, investorId: investorProfileId });
      router.push('/(investor)/investor-messages');
    } catch (err) {
      const { message, isCancelled } = handleError(err, {
        context: 'MessageFounderButton.create',
      });
      if (!isCancelled) showError('Could not open the conversation', message);
    } finally {
      setBusy(false);
    }
  };

  // Closed is worth SAYING, not hiding. An investor who cannot see why the
  // button is absent assumes the feature is broken — which is precisely the
  // impression the missing entry point gave for months.
  if (!open) {
    return (
      <View
        className="rounded-2xl p-4 flex-row items-center gap-3"
        style={{ backgroundColor: colors.surface.secondary }}
      >
        <MessageCircle size={20} color={colors.text.tertiary} />
        <Text
          style={{ color: colors.text.tertiary }}
          className="text-sm flex-1"
          numberOfLines={2}
        >
          {closedReason}
        </Text>
      </View>
    );
  }

  return (
    <View
      className="rounded-2xl overflow-hidden"
      style={{ height: 52, backgroundColor: colors.primary[600] }}
    >
      {/* Dimensions on the wrapper: a callback `style` on Pressable does not
          compute height reliably on RN 0.81+. */}
      <AnimatedPressable
        onPress={handlePress}
        disabled={busy}
        accessibilityRole="button"
        accessibilityLabel={`Message the founder of ${startupName}`}
        className="flex-1 flex-row items-center justify-center gap-2 px-4"
      >
        {busy ? (
          <ActivityIndicator size="small" color="white" />
        ) : (
          <MessageCircle size={20} color="white" />
        )}
        <Text className="text-white font-semibold" numberOfLines={1}>
          {busy ? 'Opening…' : `Message ${startupName}`}
        </Text>
      </AnimatedPressable>
    </View>
  );
}
